import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";
import { useGuest } from "@/lib/GuestContext";
import { useReveal } from "@/lib/useReveal";
import { PhotoFrame, Stamp, Squiggle } from "@/components/Motifs";

const BANDS = [
  { bg: "#FFF9F0", fg: "#252122", acc: "#FF6657", tone: "#F7C9D0" },
  { bg: "#F7C9D0", fg: "#252122", acc: "#A62D58", tone: "#FFF9F0" },
  { bg: "#3458B5", fg: "#FFF9F0", acc: "#F7B733", tone: "#F7B733" },
  { bg: "#A9C79D", fg: "#252122", acc: "#44253D", tone: "#FFF9F0" },
  { bg: "#21191E", fg: "#FFF9F0", acc: "#C6A46C", tone: "#44253D" },
  { bg: "#F7B733", fg: "#252122", acc: "#A62D58", tone: "#FF6657" },
  { bg: "#FF6657", fg: "#252122", acc: "#FFF9F0", tone: "#F7C9D0" },
];

export default function Story() {
  const [chapters, setChapters] = useState([]);
  const { guest } = useGuest();
  useEffect(() => { api.get("/public/story").then((r) => setChapters(r.data || [])); }, []);
  return (
    <div data-testid="story-page">
      <section className="bg-[#252122] text-[#FFF9F0] relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-24 md:py-36">
          <div className="kicker text-[#FF6657] rise">Our story</div>
          <h1 className="font-display text-6xl md:text-[8.5rem] leading-[0.85] tracking-[-0.04em] mt-4 rise d1">Previously,<br />on <span className="italic text-[#F7B733]">NiGi...</span></h1>
          <p className="font-hand text-2xl md:text-3xl mt-8 text-[#F7C9D0] rise d2">a rom-com in {chapters.length || 7} episodes. no spoilers. okay, one spoiler: it's happening.</p>
        </div>
        <Stamp className="absolute right-6 bottom-6 w-28 h-28 md:w-44 md:h-44 hidden sm:block" color="#F7B733" center="EP." text="NOW STREAMING • PREVIOUSLY ON NIGI • " />
      </section>
      {chapters.map((c, i) => <Episode key={c.id || i} c={c} i={i} />)}
      <section className="bg-[#FFF9F0] py-24 text-center px-5">
        <div className="font-hand text-2xl text-[#A62D58]">to be continued...</div>
        <div className="font-display italic text-6xl md:text-8xl mt-3">03.12.2026</div>
        <Link to={guest?.events?.length ? "/celebrations" : "/#invitation"} data-testid="story-finale-cta" className="btn-pop mt-10 inline-flex items-center gap-2 bg-[#FF6657] text-[#252122] rounded-full px-7 py-4 kicker">{guest?.events?.length ? "Watch the finale live" : "Open your invitation"} <ArrowUpRight size={16} /></Link>
      </section>
    </div>
  );
}

function Episode({ c, i }) {
  const b = BANDS[i % BANDS.length];
  const r = useReveal();
  const flip = i % 2 === 1;
  return (
    <section data-testid={`chapter-${i + 1}`} style={{ background: b.bg, color: b.fg }} className="relative overflow-hidden">
      <div ref={r} className="reveal max-w-7xl mx-auto px-5 md:px-10 py-20 md:py-28 grid md:grid-cols-12 gap-10 md:gap-16 items-center">
        <div className={`md:col-span-7 relative ${flip ? "md:order-2" : ""}`}>
          <div className="outline-num font-display text-[9rem] md:text-[14rem] leading-none absolute -top-16 md:-top-24 opacity-30 select-none" style={{ [flip ? "right" : "left"]: 0 }} aria-hidden="true">{String(i + 1).padStart(2, "0")}</div>
          <div className="relative">
            <div className="kicker" style={{ color: b.acc }}>Episode {String(i + 1).padStart(2, "0")}</div>
            <h2 className="font-display text-4xl md:text-6xl leading-[0.95] mt-4">{c.title}</h2>
            <p className="text-lg md:text-xl leading-relaxed mt-6 max-w-xl opacity-85">{c.description}</p>
          </div>
        </div>
        <div className={`md:col-span-5 flex ${flip ? "md:order-1 justify-start" : "justify-end"}`}>
          <div className="relative w-[78%] md:w-[85%]">
            <PhotoFrame src={c.image_url} initial={String(i + 1).padStart(2, "0")} tone={b.tone} rotate={flip ? -4 : 4} caption={c.title.split(":")[0].toLowerCase()} />
            <Squiggle width={70} className={`absolute -bottom-6 ${flip ? "-right-10" : "-left-10 -scale-x-100"}`} />
          </div>
        </div>
      </div>
    </section>
  );
}
